import { VEGA_LITE_CALCULATE_LIMITS } from './vega-lite-resource-limits';

type ScalarBounds = {
  kind: 'number' | 'boolean' | 'string' | 'scalar';
  maxStringLength: number;
};

export type VegaLiteExpressionValueBounds = ScalarBounds | { kind: 'unknown' };
export type VegaLiteExpressionFields = ReadonlyMap<string, VegaLiteExpressionValueBounds>;

export const VEGA_LITE_UNKNOWN_VALUE: VegaLiteExpressionValueBounds = { kind: 'unknown' };
export const VEGA_LITE_NUMBER_VALUE: ScalarBounds = { kind: 'number', maxStringLength: 32 };

const BOOLEAN_VALUE: ScalarBounds = { kind: 'boolean', maxStringLength: 5 };
const INPUT_VALUE: ScalarBounds = {
  kind: 'scalar',
  maxStringLength: VEGA_LITE_CALCULATE_LIMITS.maxInputStringLength,
};

const NUMERIC_FUNCTIONS = new Set([
  'abs',
  'acos',
  'asin',
  'atan',
  'atan2',
  'ceil',
  'clamp',
  'cos',
  'exp',
  'floor',
  'hypot',
  'log',
  'max',
  'min',
  'pow',
  'random',
  'round',
  'sin',
  'sqrt',
  'tan',
  'parseFloat',
  'parseInt',
  'toNumber',
  'length',
  'indexof',
  'lastindexof',
  'now',
  'utc',
  'date',
  'day',
  'year',
  'month',
  'hours',
  'minutes',
  'seconds',
  'milliseconds',
  'time',
  'timezoneoffset',
  'utcdate',
  'utcday',
  'utcyear',
  'utcmonth',
  'utchours',
  'utcminutes',
  'utcseconds',
  'utcmilliseconds',
]);
const BOOLEAN_FUNCTIONS = new Set([
  'isArray',
  'isBoolean',
  'isDate',
  'isDefined',
  'isFinite',
  'isNaN',
  'isNumber',
  'isObject',
  'isRegExp',
  'isString',
  'isTuple',
  'isValid',
  'inrange',
  'toBoolean',
]);
const NUMERIC_CONSTANTS = new Set([
  'E',
  'LN2',
  'LN10',
  'LOG2E',
  'LOG10E',
  'PI',
  'SQRT1_2',
  'SQRT2',
  'NaN',
  'MIN_VALUE',
  'MAX_VALUE',
]);
const NUMERIC_OPERATORS = new Set(['-', '*', '/', '%', '**', '&', '|', '^', '<<', '>>', '>>>']);
const COMPARISON_OPERATORS = new Set(['==', '!=', '===', '!==', '<', '<=', '>', '>=']);
const UNSAFE_FIELDS = new Set(['__proto__', 'constructor', 'prototype']);

type AstNode = Record<string, unknown>;

function isNode(value: unknown): value is AstNode {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function canBeString(value: ScalarBounds): boolean {
  return value.kind === 'string' || value.kind === 'scalar';
}

function primitiveStringCoercionLength(value: ScalarBounds): number {
  return value.kind === 'string'
    ? 0
    : Math.min(value.maxStringLength, VEGA_LITE_NUMBER_VALUE.maxStringLength);
}

function union(
  left: VegaLiteExpressionValueBounds,
  right: VegaLiteExpressionValueBounds,
): VegaLiteExpressionValueBounds {
  if (left.kind === 'unknown' || right.kind === 'unknown') {
    return VEGA_LITE_UNKNOWN_VALUE;
  }

  return {
    kind: left.kind === right.kind ? left.kind : 'scalar',
    maxStringLength: Math.max(left.maxStringLength, right.maxStringLength),
  };
}

export function getVegaLiteFieldBounds(
  fields: VegaLiteExpressionFields,
  name: string,
): VegaLiteExpressionValueBounds {
  return UNSAFE_FIELDS.has(name) ? VEGA_LITE_UNKNOWN_VALUE : fields.get(name) ?? INPUT_VALUE;
}

function literalNumber(value: unknown): number | undefined {
  return isNode(value) && value.type === 'Literal' && typeof value.value === 'number'
    ? value.value
    : undefined;
}

function memberName(node: AstNode): unknown {
  const property = node.property;

  if (!isNode(property)) {
    return undefined;
  }

  if (node.computed) {
    return property.type === 'Literal' ? property.value : undefined;
  }

  return property.type === 'Identifier' ? property.name : undefined;
}

class AllocationIssue extends Error {}

type AllocationResult =
  | { status: 'bounded'; value: VegaLiteExpressionValueBounds; allocation: number }
  | { status: 'unbounded'; detail: string };

/**
 * Abstract interpretation only. The expression policy checks syntax, functions and AST complexity
 * before this recursive pass. Prepared inputs are flat primitives with a checked string ceiling.
 * Unknown signals, containers, functions and transform outputs must not acquire that input bound.
 * Non-retaining expressions may inspect unknown values, but string-building operations still need
 * proven input bounds. Reading a field or a constant in a predicate does not allocate a copy.
 */
export function estimateVegaLiteExpressionAllocation(
  ast: unknown,
  fields: VegaLiteExpressionFields,
  { retainResult = true }: { retainResult?: boolean } = {},
): AllocationResult {
  let allocation = 0;
  const scope = retainResult ? 'Calculate' : 'Expression';

  function charge(length: number) {
    allocation += length;

    if (allocation > VEGA_LITE_CALCULATE_LIMITS.maxAllocatedStringLength) {
      throw new AllocationIssue(
        `${scope} string allocation must not exceed ${VEGA_LITE_CALCULATE_LIMITS.maxAllocatedStringLength} code units per row, including intermediate values`,
      );
    }
  }

  function string(length: number, coercion = 0): ScalarBounds {
    if (length > VEGA_LITE_CALCULATE_LIMITS.maxStringLength) {
      throw new AllocationIssue(
        `${scope} string values must not exceed ${VEGA_LITE_CALCULATE_LIMITS.maxStringLength} code units`,
      );
    }

    charge(length + coercion);
    return { kind: 'string', maxStringLength: length };
  }

  function known(value: VegaLiteExpressionValueBounds): ScalarBounds {
    if (value.kind === 'unknown') {
      throw new AllocationIssue(`${scope} value has no proven primitive string bound`);
    }

    return value;
  }

  function reference(value: VegaLiteExpressionValueBounds): VegaLiteExpressionValueBounds {
    return retainResult ? known(value) : value;
  }

  function visit(value: unknown): VegaLiteExpressionValueBounds {
    if (!isNode(value)) {
      throw new AllocationIssue(`${scope} value has no proven allocation bound`);
    }

    switch (value.type) {
      case 'Literal':
        if (typeof value.value === 'string') {
          return retainResult
            ? string(value.value.length)
            : { kind: 'string', maxStringLength: value.value.length };
        }

        return typeof value.value === 'boolean' ? BOOLEAN_VALUE : VEGA_LITE_NUMBER_VALUE;

      case 'Identifier':
        if (typeof value.name === 'string' && NUMERIC_CONSTANTS.has(value.name)) {
          return VEGA_LITE_NUMBER_VALUE;
        }

        if (!retainResult) {
          return VEGA_LITE_UNKNOWN_VALUE;
        }

        throw new AllocationIssue(
          'Dynamic signals and whole datum values have no calculate string bound',
        );

      case 'MemberExpression': {
        const name = memberName(value);

        if (!retainResult && value.computed) {
          visit(value.property);
        }

        if (
          isNode(value.object) &&
          value.object.type === 'Identifier' &&
          value.object.name === 'datum'
        ) {
          if (typeof name !== 'string') {
            if (!retainResult) {
              return VEGA_LITE_UNKNOWN_VALUE;
            }

            throw new AllocationIssue('Calculate datum fields must use a literal field name');
          }

          return reference(getVegaLiteFieldBounds(fields, name));
        }

        const objectNode = value.object;

        if (
          isNode(objectNode) &&
          objectNode.type === 'CallExpression' &&
          isNode(objectNode.callee) &&
          objectNode.callee.type === 'Identifier' &&
          objectNode.callee.name === 'split'
        ) {
          const args = Array.isArray(objectNode.arguments) ? objectNode.arguments : [];
          const values = args.map(visit);
          const separator = args[1];

          if (
            values[0] === undefined ||
            !isNode(separator) ||
            separator.type !== 'Literal' ||
            typeof separator.value !== 'string' ||
            (name !== 'length' &&
              !(typeof name === 'number' && Number.isInteger(name) && name >= 0))
          ) {
            throw new AllocationIssue(
              'Calculate split requires a literal string separator and an immediate literal index or length',
            );
          }

          const length = known(values[0]).maxStringLength;
          // Splitting a primitive string yields at most length + 1 pieces. Charge both string
          // copies and array entries, and never allow the container to escape into a datum field.
          charge(2 * length + length + 1);
          return name === 'length'
            ? VEGA_LITE_NUMBER_VALUE
            : { kind: 'scalar', maxStringLength: Math.max(9, length) };
        }

        const object = visit(value.object);

        if (name === 'length') {
          return VEGA_LITE_NUMBER_VALUE;
        }

        if (
          typeof name === 'number' &&
          Number.isInteger(name) &&
          name >= 0 &&
          object.kind !== 'unknown' &&
          canBeString(object)
        ) {
          return { kind: 'scalar', maxStringLength: 9 };
        }

        if (!retainResult) {
          return VEGA_LITE_UNKNOWN_VALUE;
        }

        throw new AllocationIssue(
          'Nested or dynamic calculate field access has no proven string bound',
        );
      }

      case 'UnaryExpression':
        visit(value.argument);

        if (value.operator === '!') {
          return BOOLEAN_VALUE;
        }

        if (value.operator === 'typeof') {
          return string(9);
        }

        if (value.operator === '+' || value.operator === '-' || value.operator === '~') {
          return VEGA_LITE_NUMBER_VALUE;
        }

        break;

      case 'LogicalExpression':
        return union(visit(value.left), visit(value.right));

      case 'BinaryExpression': {
        const left = visit(value.left);
        const right = visit(value.right);
        const operator = value.operator;

        if (operator === '+') {
          const boundedLeft = known(left);
          const boundedRight = known(right);

          if (!canBeString(boundedLeft) && !canBeString(boundedRight)) {
            return VEGA_LITE_NUMBER_VALUE;
          }

          const length = boundedLeft.maxStringLength + boundedRight.maxStringLength;
          const result = string(
            length,
            primitiveStringCoercionLength(boundedLeft) +
              primitiveStringCoercionLength(boundedRight),
          );
          return boundedLeft.kind === 'string' || boundedRight.kind === 'string'
            ? result
            : { ...result, kind: 'scalar' };
        }

        if (typeof operator === 'string' && COMPARISON_OPERATORS.has(operator)) {
          return BOOLEAN_VALUE;
        }

        if (typeof operator === 'string' && NUMERIC_OPERATORS.has(operator)) {
          return VEGA_LITE_NUMBER_VALUE;
        }

        break;
      }

      case 'ConditionalExpression':
        visit(value.test);
        return union(visit(value.consequent), visit(value.alternate));

      case 'CallExpression': {
        const name =
          isNode(value.callee) && value.callee.type === 'Identifier'
            ? value.callee.name
            : undefined;
        const args = Array.isArray(value.arguments) ? value.arguments : [];
        const values = args.map(visit);

        if (typeof name !== 'string') {
          throw new AllocationIssue('Only known expression functions have allocation bounds');
        }

        if (NUMERIC_FUNCTIONS.has(name)) {
          return VEGA_LITE_NUMBER_VALUE;
        }

        if (BOOLEAN_FUNCTIONS.has(name)) {
          return BOOLEAN_VALUE;
        }

        if (name === 'if' && values.length === 3) {
          return union(values[1], values[2]);
        }

        if (!retainResult && name === 'gradient') {
          // The expression policy separately bounds gradient counts. Its object result must never
          // acquire a primitive string bound, but all argument allocations above still count.
          return VEGA_LITE_UNKNOWN_VALUE;
        }

        if (!retainResult && (name === 'datetime' || name === 'toDate')) {
          charge(1);
          return VEGA_LITE_UNKNOWN_VALUE;
        }

        const input = values[0] === undefined ? undefined : known(values[0]);

        if (input !== undefined) {
          if (!retainResult && name === 'split') {
            const separator = args[1];

            if (
              !isNode(separator) ||
              separator.type !== 'Literal' ||
              typeof separator.value !== 'string'
            ) {
              throw new AllocationIssue('Expression split requires a literal string separator');
            }

            charge(3 * input.maxStringLength + 1);
            return VEGA_LITE_UNKNOWN_VALUE;
          }

          if (!retainResult && name === 'truncate') {
            const ellipsis = values[3] === undefined ? 1 : known(values[3]).maxStringLength;
            const limit = literalNumber(args[1]);
            const length =
              limit !== undefined && Number.isInteger(limit) && limit >= 0
                ? Math.min(input.maxStringLength, limit)
                : input.maxStringLength;

            return string(length + ellipsis, input.maxStringLength);
          }

          if (name === 'upper' || name === 'lower') {
            // Unicode case conversion can expand a code unit into multiple code units.
            return string(input.maxStringLength * 3, input.maxStringLength);
          }

          if (name === 'trim' || name === 'toString') {
            return string(input.maxStringLength, input.maxStringLength);
          }

          if (name === 'substring' || name === 'slice') {
            const start = literalNumber(args[1]);
            const end = literalNumber(args[2]);
            let length = input.maxStringLength;

            if (start !== undefined && Number.isInteger(start) && start >= 0) {
              if (end === undefined && args.length === 2) {
                length = Math.max(0, length - start);
              } else if (end !== undefined && Number.isInteger(end) && end >= 0) {
                length = Math.min(
                  length,
                  name === 'substring' ? Math.abs(end - start) : Math.max(0, end - start),
                );
              }
            }

            return string(length, input.maxStringLength);
          }

          if (name === 'format') {
            // Default-locale finite doubles, grouping, precision and bounded padding fit in 512.
            // The "c" format preserves string input; include room for a prefix/suffix as well.
            return string(Math.max(512, input.maxStringLength + 32), input.maxStringLength);
          }

          if (name === 'timeFormat' || name === 'utcFormat') {
            const format = args[1];

            if (isNode(format) && format.type === 'Literal' && typeof format.value === 'string') {
              return string(Math.max(4, format.value.length * 64), input.maxStringLength);
            }
          }
        }

        throw new AllocationIssue(
          `${scope} function "${name}" has no proven primitive string bound`,
        );
      }

      case 'ArrayExpression': {
        if (!retainResult && Array.isArray(value.elements)) {
          value.elements.forEach(visit);
          charge(value.elements.length);
          return VEGA_LITE_UNKNOWN_VALUE;
        }

        throw new AllocationIssue(
          'Calculate arrays and objects are not supported; use primitive fields',
        );
      }

      case 'ObjectExpression': {
        if (!retainResult && Array.isArray(value.properties)) {
          value.properties.forEach((property) => {
            if (!isNode(property)) {
              throw new AllocationIssue('Expression object property has no allocation bound');
            }

            visit(property.value);
          });
          charge(value.properties.length);
          return VEGA_LITE_UNKNOWN_VALUE;
        }

        throw new AllocationIssue(
          'Calculate arrays and objects are not supported; use primitive fields',
        );
      }
    }

    throw new AllocationIssue(`${scope} expression has no proven primitive allocation bound`);
  }

  try {
    const value = visit(ast);

    if (retainResult && value.kind !== 'unknown' && canBeString(value)) {
      // Direct field aliases also retain a value, even when the expression makes no new string.
      charge(Math.max(0, value.maxStringLength - allocation));
    }

    return { allocation, status: 'bounded', value };
  } catch (error) {
    return {
      status: 'unbounded',
      detail:
        error instanceof AllocationIssue
          ? error.message
          : `${scope} expression has no proven allocation bound`,
    };
  }
}
